'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useSelectedStore } from '@/hooks';
import { TreezSpecials } from '@/types';
import { useSearchService } from '@/opensearch';

import { bogoGroupKey } from './utils';

/** OpenSearch group membership for a deal — shared React Query cache (`BogoProducts`). */
const useSpecialGroupData = (special: TreezSpecials | undefined) => {
  const { store } = useSelectedStore();
  const { getProductsIdsByGroupId } = useSearchService();

  const independentGroupIds = special?.groupsRequired ?? [];
  const groupIds = useMemo(
    () => [...independentGroupIds, ...(special?.groups ?? [])],
    [special?.id],
  );

  const { groupData, isLoading } = useQueries({
    queries: groupIds.map(groupId => ({
      queryKey: ['BogoProducts', groupId, store?.short_name],
      queryFn: () => getProductsIdsByGroupId({ groupId }),
      enabled: Boolean(groupId && store?.short_name && special?.id),
    })),
    combine: results => ({
      groupData: results.reduce<Record<string, string[]>>(
        (result, current, index) => {
          const actives = current?.data;

          if (!actives?.length) {
            return result;
          }

          return {
            ...result,
            [bogoGroupKey(index, independentGroupIds.length)]: actives,
          };
        },
        {},
      ),
      isLoading: results.some(result => result.isPending),
    }),
  });

  return { groupData, isLoading };
};

export { useSpecialGroupData };
